import React, { useState } from 'react';
import { 
  Zap, 
  Hash, 
  CheckCircle, 
  X, Eye,
  TrendingUp, 
  Search, 
  Clock, 
  Star, 
  Shield, 
  AlertTriangle, 
  BarChart 
} from 'lucide-react';

const QuickAccessModal = ({ onClose, userName = "User", securityStatus = "Good" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  
  const statusColors = {
    "Critical": "text-red-400",
    "Warning": "text-amber-400",
    "Good": "text-green-400",
    "Excellent": "text-blue-400"
  };
  
  const categories = [
    { id: 'all', name: 'All Tools' },
    { id: 'security', name: 'Security' },
    { id: 'network', name: 'Network' },
    { id: 'compliance', name: 'Compliance' }
  ];



  const quickAccessItems = [
    {
      icon: <Zap className="w-6 h-6 text-blue-400" />,
      title: "Secure Deletion",
      description: "Permanently delete sensitive data.",
      category: "security",
      estimatedTime: "2 min",
      priority: "High",
      link: "/SecDel" // Add this line
    },
    {
      icon: <Hash className="w-6 h-6 text-green-400" />,
      title: "Mobile and Email Analysis",
      description: "Analysing mobile Numbers and email addresses.",
      category: "security",
      estimatedTime: "1 min",
      priority: "Medium",
      link: "/TraceMaster" // Add this line
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-purple-400" />,
      title: "Phishing Analysis",
      description: "Evaluate phishing threats and vulnerabilities.",
      category: "security",
      estimatedTime: "3 min",
      priority: "High",
      link: "/Phishing" // Add this line
    },
    {
      icon: <Shield className="w-6 h-6 text-red-500" />,
      title: "Hashing",
      description: "Ensure  your data with intergrity",
      category: "security",
      estimatedTime: "3 min",
      priority: "Medium",
      link: "/hashing" // Add this line
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-amber-400" />,
      title: "Cryptography",
      description: "Utilize cryptographic methods for data protection.",
      category: "security",
      estimatedTime: "5 min",
      priority: "Critical",
      link: "/crypto" // Add this line
    },
    {
      icon: <Eye className="w-6 h-6 text-yellow-400" />, 
      title: "ThreatEye",
      description: "Scan for vulnerabilities in web applications.",
      category: "security",
      estimatedTime: "5 min",
      priority: "High",
      link: "/VScanner" 
    },
    {
      icon: <BarChart className="w-6 h-6 text-indigo-400" />,
      title: "EduConnect",
      description: "Secure connection for educational platforms.",
      category: "compliance",
      estimatedTime: "1 min",
      priority: "Low",
      link: "/EduConnect" // Add this line
    },
    {
      icon: <Clock className="w-6 h-6 text-blue-400" />,
      title: "Backup",
      description: "Ensure your data is backed up securely.",
      category: "network",
      estimatedTime: "1 min",
      priority: "Medium",
      link: "/Backup" // Add this line
    },   
    
    {
      icon: <BarChart className="w-6 h-6 text-gray-400" />,
      title: "Network Diagnostic",
      description: "Analyze and diagnose network issues.",
      category: "network",
      estimatedTime: "5 min",
      priority: "Medium",
      link: "/CognitoNet" // Add this line
    }
  ];
  
  // Update the handleRunTool function to handle navigation
  const handleRunTool = (toolName, link) => {
    console.log(`Running ${toolName}`);
    // Navigate to the tool's page
    window.location.href = link;
  };
  
  const filteredItems = quickAccessItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });
  
    
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 bg-opacity-90 rounded-2xl w-full max-w-3xl p-6 relative shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header with close button and status */}
        <div className="flex justify-between items-center border-b border-gray-700 pb-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center">
              Security Center
              <span className={`ml-3 text-sm px-3 py-1 rounded-full ${
                securityStatus === "Critical" ? "bg-red-600" :
                securityStatus === "Warning" ? "bg-amber-500" :
                "bg-green-600"
              }`}>
                <span className={`font-medium ${statusColors[securityStatus]}`}>
                  Status: {securityStatus}
                </span>
              </span>
            </h2>
            <p className="text-gray-300">
              Welcome back, {userName}. Access your security tools below.
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 bg-gray-700 p-2 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Search and filter controls */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 placeholder-gray-500"
              placeholder="Search security tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex space-x-2 overflow-x-auto pb-1 no-scrollbar">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Recommended security action */}
        {securityStatus === "Warning" || securityStatus === "Critical" ? (
          <div className="mb-6 bg-amber-900/40 border-l-4 border-amber-500 p-4 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-400 font-medium">
                  Recommended action: Run a Rapid Threat Scan to identify recent security concerns.
                </p>
              </div>
            </div>
          </div>
        ) : null}
        
        {/* Grid layout for tools */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <div 
                key={index} 
                className="bg-gray-800 border border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group"
              >
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 p-2 bg-gray-700 rounded-lg mr-4">
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors">
                          {item.title}
                        </h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          item.priority === "Critical" ? "bg-red-600 text-red-200" :
                          item.priority === "High" ? "bg-amber-600 text-amber-200" :
                          item.priority === "Medium" ? "bg-blue-600 text-blue-200" :
                          "bg-gray-600 text-gray-200"
                        }`}>
                          {item.priority}
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm mb-2">
                        {item.description}
                      </p>
                      <div className="flex items-center text-xs text-gray-400">
                        <Clock className="w-3 h-3 mr-1" /> Est. time: {item.estimatedTime}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-700 bg-gray-900 p-2">
                <button 
                  onClick={() => handleRunTool(item.title, item.link)}
                  className="w-full py-1.5 text-sm bg-transparent hover:bg-blue-500 text-gray-300 hover:text-white font-medium transition-colors duration-300 rounded-lg flex items-center justify-center"
                >
                  <Zap className="w-4 h-4 mr-1.5" />
                  Run Now
                </button>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-2 flex flex-col items-center justify-center p-8 text-center text-gray-500">
              <Search className="w-10 h-10 mb-2 opacity-50" />
              <p>No matching security tools found.</p>
              <p className="text-sm">Try adjusting your search or filters.</p>
            </div>
          )}
        </div>
        
        {/* Footer with quick links */}
        <div className="pt-4 border-t border-gray-700">
          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              <button className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors">
                <Clock className="w-5 h-5" />
              </button>
              <button className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors">
                <Star className="w-5 h-5" />
              </button>
              <button className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors">
                <BarChart className="w-5 h-5" />
              </button>
            </div>
            <button className="text-sm font-medium text-blue-500 hover:text-blue-400">
              View Security Dashboard →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAccessModal;